import React from "react";
import { motion } from "framer-motion";

const GameDetailsSkeleton = () => {
  return (
    <div className="min-h-screen relative">
      {/* Hero Section Skeleton */}
      <div className="fixed inset-0 w-full h-full bg-gradient-to-br from-gray-800/50 to-gray-900/50 -z-10"></div>
      <div className="absolute inset-0 backdrop-blur-md bg-gradient-to-r from-black/60 to-transparent z-10"></div>
      
      <div className="mx-auto w-[95%] sm:w-[92%] md:w-[90%] lg:max-w-[80%] py-8 relative z-20">
        <div className="relative overflow-hidden mt-[50px] mb-5">
          {/* Back Button Skeleton */}
          <div className="absolute inset-0 z-30 top-2 left-2">
            <div className="w-8 h-8 bg-gray-600/50 rounded-full animate-pulse"></div>
          </div>

          {/* Hero Image/Video Skeleton */}
          <div className="w-full h-96 bg-gradient-to-br from-gray-700/50 to-gray-800/50 rounded-lg animate-pulse"></div>

          {/* Game Title Overlay Skeleton */}
          <div className="absolute inset-0 flex items-end">
            <div className="p-8 text-white">
              <div className="h-8 bg-gray-600/50 rounded animate-pulse w-64 mb-2"></div>
              <div className="h-4 bg-gray-600/50 rounded animate-pulse w-96 mb-2"></div>
              <div className="h-4 bg-gray-600/50 rounded animate-pulse w-80"></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 xl:gap-8">
          {/* Main Content Skeleton */}
          <div className="lg:col-span-2 space-y-4 md:space-y-8">
            {/* Action Buttons Skeleton */}
            <div className="flex flex-wrap gap-4">
              <div className="h-12 bg-gray-600/50 rounded-md animate-pulse w-32"></div>
              <div className="h-12 bg-gray-600/50 rounded-md animate-pulse w-36"></div>
              <div className="h-12 bg-gray-600/50 rounded-md animate-pulse w-24"></div>
            </div>

            {/* Mobile Platform Tabs Skeleton */}
            <div className="bg-black/50 rounded-xl py-3 px-4 md:px-6 backdrop-blur-sm border border-white/40 block lg:hidden">
              <div className="flex space-x-4 mb-2">
                <div className="w-8 h-8 bg-gray-600/50 rounded animate-pulse"></div>
                <div className="w-8 h-8 bg-gray-600/50 rounded animate-pulse"></div>
                <div className="w-8 h-8 bg-gray-600/50 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-600/50 rounded animate-pulse w-20"></div>
                  <div className="h-4 bg-gray-600/50 rounded animate-pulse w-16"></div>
                </div>
                <div className="w-24 h-10 bg-gray-600/50 rounded-md animate-pulse"></div>
              </div>
            </div>

            {/* Tabs Skeleton */}
            <div className="border-b border-white/40">
              <div className="flex space-x-8">
                <div className="h-12 bg-gray-600/50 rounded animate-pulse w-20"></div>
                <div className="h-12 bg-gray-600/50 rounded animate-pulse w-28"></div>
                <div className="h-12 bg-gray-600/50 rounded animate-pulse w-20"></div>
              </div>
            </div>

            {/* Tab Content Skeleton */}
            <div className="bg-black/50 rounded-xl p-4 md:p-6 backdrop-blur-sm border border-white/40">
              <div className="space-y-6">
                {/* Title Skeleton */}
                <div className="h-8 bg-gray-600/50 rounded animate-pulse w-48 mb-4"></div>
                
                {/* Description Skeleton */}
                <div className="space-y-2">
                  <div className="h-4 bg-gray-600/50 rounded animate-pulse"></div>
                  <div className="h-4 bg-gray-600/50 rounded animate-pulse w-5/6"></div>
                  <div className="h-4 bg-gray-600/50 rounded animate-pulse w-4/6"></div>
                  <div className="h-4 bg-gray-600/50 rounded animate-pulse w-3/4"></div>
                </div>

                {/* Tags Skeleton */}
                <div>
                  <div className="h-5 bg-gray-600/50 rounded animate-pulse w-16 mb-3"></div>
                  <div className="flex flex-wrap gap-2">
                    <div className="w-16 h-6 bg-gray-600/50 rounded-full animate-pulse"></div>
                    <div className="w-20 h-6 bg-gray-600/50 rounded-full animate-pulse"></div>
                    <div className="w-14 h-6 bg-gray-600/50 rounded-full animate-pulse"></div>
                  </div>
                </div>

                {/* Key Features Skeleton */}
                <div>
                  <div className="h-6 bg-gray-600/50 rounded animate-pulse w-32 mb-3"></div>
                  <div className="space-y-2">
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-gray-600/50 rounded-full mt-2 animate-pulse"></div>
                      <div className="h-4 bg-gray-600/50 rounded animate-pulse w-3/4"></div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-gray-600/50 rounded-full mt-2 animate-pulse"></div>
                      <div className="h-4 bg-gray-600/50 rounded animate-pulse w-2/3"></div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-gray-600/50 rounded-full mt-2 animate-pulse"></div>
                      <div className="h-4 bg-gray-600/50 rounded animate-pulse w-4/5"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Skeleton */}
          <div className="space-y-6">
            {/* Platform Tabs Skeleton */}
            <div className="bg-black/50 rounded-xl py-3 px-3 xl:px-6 backdrop-blur-sm border border-white/40 hidden lg:block">
              <div className="flex space-x-4 mb-2">
                <div className="w-8 h-8 bg-gray-600/50 rounded animate-pulse"></div>
                <div className="w-8 h-8 bg-gray-600/50 rounded animate-pulse"></div>
                <div className="w-8 h-8 bg-gray-600/50 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-600/50 rounded animate-pulse w-20"></div>
                  <div className="h-4 bg-gray-600/50 rounded animate-pulse w-16"></div>
                </div>
                <div className="w-24 h-10 bg-gray-600/50 rounded-md animate-pulse"></div>
              </div>
            </div>

            {/* Game Images Skeleton */}
            <div className="bg-black/30 rounded-2xl p-6 backdrop-blur-sm border border-white/20">
              <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
                {Array.from({ length: 8 }, (_, index) => (
                  <div key={index} className="break-inside-avoid mb-4">
                    <div className="relative overflow-hidden rounded-lg bg-gray-800 shadow-lg">
                      <div className="w-full h-32 bg-gray-600/50 animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameDetailsSkeleton;
